import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, from, map, mergeMap, of } from 'rxjs';

import { normalizeSearch } from '@app/hr/hr-leave-board';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { PartnerService } from '@app/common/partner/partner.service';
import { openPartnerSheet } from '@app/common/partner/partner-sheet';
import { RentalBillingService, serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import type { RentalLifecycleFull } from '@app/sales/rental-billing/rental-billing.model';
import { INTERRUPTED_BADGE, statusClass, statusHint, statusLabel } from '@app/sales/rental-billing/rental-billing.labels';
import { partnerLabel } from '@app/sales/rental-billing/planning/rental-planning.view';
import { frDay, money } from '@app/sales/rental-billing/rental-days';
import { getOrderLabel } from '@app/shared/constants';
import { pickedDateStr, todayStr, yearsBefore } from '@app/shared/time';

import { PayerCounts, PayerDossier, PayerRow, countsOf, dossierOf, filterRows, headMatches, isFinished, rowsOf, sameStarts } from './rental-payer.model';
import { PayerOrderWire, PayerOrdersCounts, PayerOrdersQuery, RentalPayerService } from './rental-payer.service';
import { RentalPayerPlanningComponent } from './rental-payer-planning.component';

type Display = 'table' | 'planning';
type Status = 'loading' | 'ok' | 'failed';
type Period = 'day' | 'month' | 'days30' | 'months12';

interface DossierVM {
  d: PayerDossier;
  key: string;                  // id + mode de lecture : le Planning est recréé quand on coche « Dossiers repris »
  open: boolean;                // vue sans les matériels repris (dossier en cours, case « Dossiers repris » décochée)
  cycle: RentalLifecycleFull | null;
  loading: boolean;
  error: string | null;
  rows: PayerRow[];
  shownRows: PayerRow[];
  same: number[];               // autres dossiers du même patient (chapeau en double)
}

const CYCLES_AT_ONCE = 4; // vues du moteur demandées en parallèle, pour les seuls dossiers de la page
const NO_COUNTS: PayerOrdersCounts = { open: 0, returned: 0, closed: 0, canceled: 0 };

@Component({
  selector: 'app-rental-payer',
  templateUrl: './rental-payer.component.html',
  styleUrls: ['./rental-payer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MenuCommonComponent, RentalPayerPlanningComponent, SelectDateComponent, NgTemplateOutlet, RouterLink, FormsModule, MatButtonModule, MatCheckboxModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
})
export class RentalPayerComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private payers = inject(RentalPayerService);
  private partners = inject(PartnerService);
  private billing = inject(RentalBillingService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  public readonly fr = frDay;
  public readonly m = money;
  public readonly statusLabel = statusLabel;
  public readonly statusClass = statusClass;
  public readonly statusHint = statusHint;
  public readonly orderLabel = getOrderLabel;
  public readonly INTERRUPTED_BADGE = INTERRUPTED_BADGE;

  public payerId = 0;
  public payerName = '';
  public status: Status = 'loading';
  public loadError: string | null = null;
  public display: Display = 'table';
  // À l'arrivée : dossiers en cours seulement (JP 29/10) ; repris et annulés lus en base à la demande.
  public withReturned = false;
  public withClosed = false;
  public withCanceled = false;
  public withRecipient = false;   // ?with_recipient=true (fiche tiers, #38) : aussi les dossiers dont ce tiers est le bénéficiaire
  public search = '';
  public datstart: Date | undefined;
  public datend: Date | undefined;

  private all: DossierVM[] = [];
  public shown: DossierVM[] = [];
  public found: PayerOrdersCounts = NO_COUNTS;   // dossiers du payeur pour la période et la recherche, par catégorie
  public more = false;                          // la base a encore des dossiers après la page
  public listLoading = false;
  public collapsed = new Set<number>();
  public counts: PayerCounts = { inPlace: 0, toDeliver: 0, returned: 0 };
  public patients = 0;
  public dueHt = 0;
  public creditHt = 0;
  public pending = 0;      // dossiers dont la vue du moteur n'est pas encore là
  public refused = 0;      // dossiers dont le passage serait bloqué
  public duplicates = 0;   // dossiers qui ont un autre chapeau pour le même patient

  private listSub = new Subscription();           // page de dossiers en cours de lecture
  private cycleSubs = new Subscription();         // vues du moteur en cours de lecture, coupées à chaque relecture de la liste
  private cycles = new Map<string, RentalLifecycleFull>(); // vues déjà lues, par clé : une recherche ou une case ne les recalcule pas
  private searchInput = new Subject<string>();

  ngOnInit(): void {
    this.searchInput.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load());
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      const id = Number(p.get('id'));
      this.payerId = Number.isInteger(id) && id > 0 ? id : 0;
      this.payerName = '';
      this.display = this.route.snapshot?.queryParamMap?.get('vue') === 'planning' ? 'planning' : 'table';
      this.withRecipient = this.route.snapshot?.queryParamMap?.get('with_recipient') === 'true';
      this.collapsed = new Set();
      this.cycles.clear();
      this.all = [];
      this.found = NO_COUNTS;
      this.more = false;
      this.status = 'loading';
      this.loadPayerName();
      this.load();
    });
  }

  private loadPayerName(): void {
    const payer = this.payerId;
    if (!payer) return;
    this.partners.getPartnerByid(payer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (p: any) => { if (payer === this.payerId) this.payerName = partnerLabel(p, payer); },
      error: () => { if (payer === this.payerId && !this.payerName) this.payerName = `tiers ${payer}`; },
    });
  }

  private query(before: number | null): PayerOrdersQuery {
    return {
      start: pickedDateStr(this.datstart), end: pickedDateStr(this.datend),
      returned: this.withReturned, closed: this.withClosed, canceled: this.withCanceled, q: this.search, before, withRecipient: this.withRecipient,
    };
  }

  // Première page selon les filtres ; l'ancienne liste reste affichée jusqu'à la réponse.
  load(): void {
    this.listSub.unsubscribe();
    this.cycleSubs.unsubscribe();
    this.cycleSubs = new Subscription();   // une Subscription close couperait aussitôt tout ce qu'on y ajoute
    for (const vm of this.all) vm.loading = false;
    if (!this.payerId) { this.status = 'failed'; this.loadError = 'Payeur inconnu'; return; }
    if (this.status === 'failed') this.status = 'loading';
    this.loadError = null;
    this.listLoading = true;
    const payer = this.payerId;
    this.listSub = this.payers.getOrders(payer, this.query(null)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (page) => {
        this.listLoading = false;
        this.found = page?.counts ?? NO_COUNTS;
        this.more = !!page?.more;
        this.all = this.toVMs(page?.orders ?? [], payer);
        this.status = 'ok';
        this.afterList();
      },
      error: (e) => {
        this.listLoading = false;
        this.status = 'failed';
        this.loadError = serverMessage(e);
      },
    });
  }

  // Page suivante : dossiers de numéro inférieur au dernier affiché.
  loadMore(): void {
    const last = this.all[this.all.length - 1];
    if (!last || this.listLoading) return;
    this.listLoading = true;
    const payer = this.payerId;
    this.listSub = this.payers.getOrders(payer, this.query(last.d.id)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (page) => {
        this.listLoading = false;
        this.found = page?.counts ?? this.found;
        this.more = !!page?.more;
        const known = new Set(this.all.map((vm) => vm.d.id));
        this.all = [...this.all, ...this.toVMs(page?.orders ?? [], payer).filter((vm) => !known.has(vm.d.id))];
        this.afterList();
      },
      error: (e) => {
        this.listLoading = false;
        this.loadError = serverMessage(e);
      },
    });
  }

  // Un dossier repris ou clos se lit en entier : en lecture `open`, il n'aurait aucune rangée.
  private toVMs(orders: PayerOrderWire[], payer: number): DossierVM[] {
    return orders.map((o) => dossierOf(o, payer)).filter((d) => d.id > 0).map((d) => {
      const open = !this.withReturned && !isFinished(d);
      const key = `${d.id}:${open ? 'open' : 'all'}`;
      const cycle = this.cycles.get(key) ?? null;
      return { d, key, open, cycle, loading: false, error: null, rows: cycle ? rowsOf(cycle, d.recipientName) : [], shownRows: [], same: [] };
    });
  }

  private afterList(): void {
    const same = sameStarts(this.all.map((vm) => vm.d));
    for (const vm of this.all) vm.same = same.get(vm.d.id) ?? [];
    this.loadCycles();
    this.refresh();
  }

  // Vue du moteur de chaque dossier de la page, quatre à la fois ; une relecture (`only`) garde l'ancienne vue à l'écran.
  private loadCycles(only?: DossierVM[]): void {
    const todo = only ?? this.all.filter((vm) => !vm.cycle && !vm.loading && !vm.error);
    if (!todo.length) return;
    for (const vm of todo) { vm.loading = true; vm.error = null; }
    this.cycleSubs.add(from(todo).pipe(
      mergeMap((vm) => this.billing.getLifecycle(vm.d.id, null, vm.open).pipe(
        map((cycle) => ({ vm, cycle, error: null as string | null })),
        catchError((e) => of({ vm, cycle: null as RentalLifecycleFull | null, error: serverMessage(e) as string | null })),
      ), CYCLES_AT_ONCE),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ vm, cycle, error }) => {
      vm.loading = false;
      vm.error = error;
      if (cycle) { vm.cycle = cycle; vm.rows = rowsOf(cycle, vm.d.recipientName); this.cycles.set(vm.key, cycle); }
      this.refresh();
    }));
  }

  reloadCycle(vm: DossierVM): void {
    if (!vm.loading) this.loadCycles([vm]);
  }

  refresh(): void {
    for (const vm of this.all) vm.shownRows = filterRows(vm.rows, this.search, this.headText(vm));
    this.shown = this.all.filter((vm) => headMatches(this.search, this.headText(vm), vm.d.memos) || vm.shownRows.length > 0);
    const rows = this.shown.flatMap((vm) => vm.shownRows);
    this.counts = countsOf(rows);
    this.patients = new Set(rows.map((r) => normalizeSearch(r.patient)).filter((p) => !!p)).size;
    const done = this.shown.filter((vm) => vm.cycle);
    this.dueHt = done.reduce((s, vm) => s + (vm.cycle?.amounts?.due_ht ?? 0), 0);
    this.creditHt = done.reduce((s, vm) => s + (vm.cycle?.amounts?.credit_ht ?? 0), 0);
    this.refused = done.filter((vm) => !!vm.cycle?.amounts?.refused).length;
    this.pending = this.shown.filter((vm) => vm.loading).length;
    this.duplicates = this.shown.filter((vm) => vm.same.length > 0).length;
  }

  headText(vm: DossierVM): string {
    return `${vm.d.id} ${vm.d.recipientName ?? ''}`;
  }

  // Dossiers que la base renvoie pour les cases cochées (les pages pas encore lues comprises).
  get total(): number {
    return this.found.open + (this.withReturned ? this.found.returned : 0) + (this.withClosed ? this.found.closed : 0) + (this.withCanceled ? this.found.canceled : 0);
  }

  get remaining(): number {
    return Math.max(0, this.total - this.all.length);
  }

  get hasPeriod(): boolean {
    return !!this.datstart || !!this.datend;
  }

  // Matériels du dossier lus par la vue : sans les repris tant que la case n'est pas cochée.
  listedCount(vm: DossierVM): number {
    return vm.cycle ? vm.rows.length : vm.d.rentalCount - (vm.open ? vm.d.returnedCount : 0);
  }

  // Pastille de l'en-tête : « Repris » (tout revenu, reste peut-être à facturer) ou « Clos » (toutes lignes closes).
  categoryPill(vm: DossierVM): { label: string; tip: string } | null {
    const d = vm.d;
    if (d.category === 'closed') return { label: 'Clos', tip: `Toutes les lignes louées et vendues sont closes${d.closedOn ? ', dernière clôture le ' + frDay(d.closedOn) : ''} : plus rien ne se facture` };
    if (d.category === 'returned') return { label: 'Repris', tip: `Tout le matériel est repris${d.lastReturn ? ', dernière reprise le ' + frDay(d.lastReturn) : ''} ; le dossier sera clos une fois tout facturé` };
    return null;
  }

  get periodText(): string {
    const s = pickedDateStr(this.datstart);
    const e = pickedDateStr(this.datend);
    if (s && e) return `Dossiers en cours à un moment du ${frDay(s)} au ${frDay(e)}`;
    if (s) return `Dossiers en cours le ${frDay(s)} ou après`;
    return e ? `Dossiers ouverts au plus tard le ${frDay(e)}` : '';
  }

  // --- Gestes de la page ---

  setDisplay(display: Display): void {
    if (display === this.display) return;
    this.display = display;
    this.router.navigate([], { relativeTo: this.route, queryParams: { vue: display === 'planning' ? 'planning' : null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  setReturned(on: boolean): void {
    this.withReturned = on;
    this.load();
  }

  setClosed(on: boolean): void {
    this.withClosed = on;
    this.load();
  }

  setCanceled(on: boolean): void {
    this.withCanceled = on;
    this.load();
  }

  onSearch(text: string): void {
    this.search = text;
    this.refresh();               // rangées déjà lues filtrées tout de suite, la base relue après la frappe
    this.searchInput.next(text);
  }

  dateChange(date: Date | undefined, which: 'datstart' | 'datend'): void {
    this[which] = date ?? undefined;
    this.load();
  }

  clearPeriod(): void {
    this.datstart = this.datend = undefined;
    this.load();
  }

  // Mêmes raccourcis que la page du dossier et la liste des commandes, tous jusqu'à aujourd'hui inclus.
  public readonly periods: { key: Period; label: string; title: string }[] = [
    { key: 'day', label: 'Jour', title: "Aujourd'hui" },
    { key: 'month', label: 'Mois', title: "Mois en cours : du 1er à aujourd'hui" },
    { key: 'days30', label: '30 j', title: "30 derniers jours, aujourd'hui compris" },
    { key: 'months12', label: '12 m', title: "12 derniers mois : du lendemain de la même date l'an dernier à aujourd'hui" },
  ];

  // Jour de Paris (todayStr) remis en date locale : pickedDateStr relit ces dates sans conversion de fuseau.
  private periodRange(key: Period): [Date, Date] {
    const [y, m1, d] = todayStr().split('-').map(Number);
    const m = m1 - 1;
    const today = new Date(y, m, d);
    if (key === 'month') return [new Date(y, m, 1), today];
    if (key === 'days30') return [new Date(y, m, d - 29), today];
    if (key === 'months12') { const s = yearsBefore(today, 1); s.setDate(s.getDate() + 1); return [s, today]; }
    return [new Date(y, m, d), today];
  }

  isPeriod(key: Period): boolean {
    const [s, e] = this.periodRange(key);
    return pickedDateStr(this.datstart) === pickedDateStr(s) && pickedDateStr(this.datend) === pickedDateStr(e);
  }

  setPeriod(key: Period): void {
    [this.datstart, this.datend] = this.periodRange(key);
    this.load();
  }

  toggle(id: number, e?: Event): void {
    e?.preventDefault();
    e?.stopPropagation();
    if (this.collapsed.has(id)) this.collapsed.delete(id);
    else this.collapsed.add(id);
  }

  get allCollapsed(): boolean {
    return this.shown.length > 0 && this.shown.every((vm) => this.collapsed.has(vm.d.id));
  }

  toggleAll(): void {
    this.collapsed = this.allCollapsed ? new Set() : new Set(this.shown.map((vm) => vm.d.id));
  }

  dossierQuery(): Record<string, string> {
    return this.display === 'planning' ? { vue: 'planning' } : {};
  }

  plural(n: number, one: string, many: string): string {
    return `${n} ${n > 1 ? many : one}`;
  }

  // « Même patient que n° … » : amène l'autre dossier à l'écran, déplié.
  goTo(id: number, e: Event): void {
    e.preventDefault();
    this.collapsed.delete(id);
    document.getElementById(`dossier-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Nom du payeur ou d'un bénéficiaire : sa fiche tiers en fenêtre ; au retour, le nom relu remplace l'ancien partout sur la page.
  openPartner(id: number | null, e?: Event): void {
    e?.preventDefault();
    e?.stopPropagation();
    if (!id) return;
    openPartnerSheet(this.dialog, id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      if (!p?.id) return;
      if (+p.id === this.payerId) this.payerName = partnerLabel(p, this.payerId);
      for (const vm of this.all.filter((x) => x.d.recipientId === +p.id)) {
        vm.d = { ...vm.d, recipientName: partnerLabel(p, +p.id) };
        if (vm.cycle) vm.rows = rowsOf(vm.cycle, vm.d.recipientName);
      }
      this.refresh();
    });
  }

  anomaliesTip(r: PayerRow): string {
    return r.anomalies.join('\n');
  }

  sameTip(vm: DossierVM): string {
    return `Même patient${vm.d.fse ? ', même prise en charge Sécu' : ''} que ${vm.same.length > 1 ? 'les dossiers' : 'le dossier'} n° ${vm.same.join(', ')} : un seul dossier par patient est attendu`;
  }

  trackRow(_: number, r: PayerRow): number {
    return r.id;
  }
}
